import { useAppTheme } from "@hooks/useAppTheme";
import { Box } from "@memorang/ui";
import { Fragment } from "react";
import { Button, Card, Divider, Text } from "react-native-paper";
import type { ProethicaScoreRowData } from "../../helpers/proethica-progress-data";
import { IconAvatar } from "./IconAvatar";
import { ProethicaScoreRow } from "./ProethicaScoreRow";

type Props = {
	scoreRows: ProethicaScoreRowData[];
	remaining: number;
	startingModuleId: string | null;
	onModulePress: (id: string) => void;
	onContinue: () => void;
};

export const ProethicaScoresCard = ({
	scoreRows,
	remaining,
	startingModuleId,
	onModulePress,
	onContinue,
}: Props) => {
	const theme = useAppTheme();

	return (
		<Card
			mode="outlined"
			style={{
				backgroundColor: theme.colors.surface,
				borderColor: theme.colors.outlineVariant,
				borderRadius: theme.roundness,
				overflow: "hidden",
			}}
		>
			<Box
				flexDirection="row"
				alignItems="center"
				justifyContent="space-between"
				padding={16}
				style={{ gap: 12 }}
			>
				<Box
					flexDirection="row"
					alignItems="center"
					flex={1}
					style={{ gap: 12 }}
				>
					<IconAvatar
						icon="format-list-bulleted"
						background={theme.colors.surfaceVariant}
						color={theme.colors.onSurfaceVariant}
					/>
					<Box flex={1} style={{ gap: 2 }}>
						<Text
							variant="titleMedium"
							style={{ color: theme.colors.onSurface, fontWeight: "700" }}
						>
							Knowledge Check
						</Text>
						<Text
							variant="bodySmall"
							style={{ color: theme.colors.onSurfaceVariant }}
						>
							How well you know each module
						</Text>
					</Box>
				</Box>
				{remaining > 0 ? (
					<Text
						variant="bodySmall"
						style={{ color: theme.colors.onSurfaceVariant }}
					>
						{remaining} modules pending
					</Text>
				) : null}
			</Box>

			<Divider />

			<Box>
				{scoreRows.map((row, index) => (
					<Fragment key={row.id}>
						{index > 0 ? <Divider /> : null}
						<ProethicaScoreRow
							{...row}
							loading={startingModuleId === row.id}
							onPress={() => onModulePress(row.id)}
						/>
					</Fragment>
				))}
			</Box>

			{remaining > 0 ? (
				<>
					<Divider />
					<Box
						flexDirection="row"
						alignItems="center"
						justifyContent="space-between"
						padding={16}
						style={{ gap: 12 }}
					>
						<Box flex={1} style={{ gap: 2 }}>
							<Text
								variant="titleSmall"
								style={{ color: theme.colors.onSurface, fontWeight: "700" }}
							>
								{remaining} modules remaining
							</Text>
							<Text
								variant="bodySmall"
								style={{ color: theme.colors.onSurfaceVariant }}
							>
								Keep going to finish the course.
							</Text>
						</Box>
						<Button
							mode="contained"
							onPress={onContinue}
							style={{ borderRadius: 999 }}
						>
							Continue Learning
						</Button>
					</Box>
				</>
			) : null}
		</Card>
	);
};
